import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Flame, Sparkles, Layers } from "lucide-react";
import { toast } from "sonner";

export default function FleetPromoDialog({ carCount, onSaved }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [pct, setPct] = useState(15);
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/fleet/promo", { enabled, weekend_discount_pct: Number(pct), boost_until: until || null });
      toast.success(`Promo applied to ${data.matched} car${data.matched === 1 ? "" : "s"}`);
      setOpen(false);
      onSaved?.(data.promo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="fleet-promo-btn" className="bg-transparent border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
          <Layers className="w-3.5 h-3.5 mr-1.5" /> Fleet-wide promo
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#131722] border-slate-800 max-w-md" data-testid="fleet-promo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-white">
            <Flame className="w-5 h-5 text-amber-400" /> Promote your whole fleet
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400 mt-1">Applies to all {carCount} of your cars in one click. Overrides per-car promos.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-slate-800">
            <div><Label className="text-base">Promo enabled</Label><p className="text-xs text-slate-500 mt-0.5">Turn off to clear all promos</p></div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="fleet-promo-enabled" />
          </div>
          <div>
            <Label>Weekend discount (%)</Label>
            <Input type="number" min={0} max={50} value={pct} onChange={(e) => setPct(e.target.value)} data-testid="fleet-promo-pct" className="bg-slate-900 border-slate-800 mt-1.5" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-cyan-400" /> Boost placement until</Label>
            <Input type="date" value={until?.slice(0, 10) || ""} onChange={(e) => setUntil(e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="fleet-promo-boost" className="bg-slate-900 border-slate-800 mt-1.5" />
          </div>
          <Button type="submit" disabled={busy || carCount === 0} data-testid="fleet-promo-save" className="w-full h-11 bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold">
            {busy ? "Applying…" : `Apply to ${carCount} car${carCount === 1 ? "" : "s"}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
